/**
 * Ticket Priority Constants
 *
 * Centralized definitions for ticket priorities. Keep in sync with the Postgres
 * `ticket_priority` enum (mirrored by the codegen `Ticket_Priority` union).
 *
 * Use these instead of hand-rolling `priority === "high" || priority === "critical"`
 * style checks — those drift from the enum (e.g. silently dropping `urgent`, the
 * highest level) and undercount "needs attention" buckets.
 */

import type { Ticket_Priority } from "@/src/gql/graphql";

/** All priorities, lowest → highest. */
export const TICKET_PRIORITIES = [
  "low",
  "medium",
  "high",
  "critical",
  "urgent",
] as const satisfies readonly Ticket_Priority[];

/**
 * Priorities that demand prompt attention — "high and above".
 * Includes `urgent`, which a `=== "high" || === "critical"` check drops.
 */
export const HIGH_OR_ABOVE_PRIORITIES = [
  "high",
  "critical",
  "urgent",
] as const satisfies readonly Ticket_Priority[];

/** True when a ticket's priority is high, critical, or urgent. */
export function isHighOrAbovePriority(
  priority: Ticket_Priority | string | null | undefined,
): boolean {
  return (HIGH_OR_ABOVE_PRIORITIES as readonly string[]).includes(
    (priority ?? "") as string,
  );
}
